import React, { useState, useRef, useEffect, useCallback } from 'react';
import { UtilizationDataProvider, useUtilizationContext, formatWeekDisplay, getSundayIso } from './UtilizationDataContext';
import UtilizationPreFilter from './UtilizationPreFilter';
import TicketHoursView from './TicketHoursView';
import PersonUtilizationView from './PersonUtilizationView';
import UtilizationAddModal from './UtilizationAddModal';
import useUtilizationData from '../../hooks/useUtilizationData';
import Loading from '../ui/Loading';
import ErrorBoundary from '../ui/ErrorBoundary';
import { getColorRules, saveColorRules } from '../../utils/highlightCache';
import UtilizationSoftScheduleModal from './UtilizationSoftScheduleModal';
import UtilizationSoftScheduleEditModal from './UtilizationSoftScheduleEditModal';

function formatShortDate(d) {
  if (!d) return '—';
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SoftScheduleSection({ rows, onEditRow }) {
  if (!rows || rows.length === 0) return null;
  const totalHours = rows.reduce((s, r) => s + (Number(r.hours) || 0), 0);
  return (
    <div style={{ marginTop: 16, border: '1px solid #37474f', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ padding: '6px 14px', background: '#1a3a3a', borderBottom: '1px solid #37474f', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#80cbc4', textTransform: 'uppercase', letterSpacing: 0.5 }}>Soft Schedules</span>
        <span style={{ fontSize: 11, color: '#546e7a' }}>{rows.length} entr{rows.length !== 1 ? 'ies' : 'y'}</span>
        {totalHours > 0 && <span style={{ fontSize: 11, color: '#80cbc4' }}>· {totalHours.toFixed(1)}h total</span>}
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, color: '#eceff1' }}>
        <thead>
          <tr>
            <th style={{ padding: '6px 10px', background: '#1e2e36', borderBottom: '1px solid #37474f', textAlign: 'left', fontWeight: 700, color: '#80cbc4', whiteSpace: 'nowrap' }}>Member</th>
            <th style={{ padding: '6px 10px', background: '#1e2e36', borderBottom: '1px solid #37474f', textAlign: 'left', fontWeight: 700, color: '#80cbc4' }}>Ticket / Description</th>
            <th style={{ padding: '6px 10px', background: '#1e2e36', borderBottom: '1px solid #37474f', textAlign: 'left', fontWeight: 700, color: '#80cbc4', whiteSpace: 'nowrap' }}>Dates</th>
            <th style={{ padding: '6px 10px', background: '#1e2e36', borderBottom: '1px solid #37474f', textAlign: 'right', fontWeight: 700, color: '#80cbc4', whiteSpace: 'nowrap' }}>Hours</th>
            {onEditRow && <th style={{ padding: '6px 10px', background: '#1e2e36', borderBottom: '1px solid #37474f', width: 50 }} />}
          </tr>
        </thead>
        <tbody>
          {rows.map((ss, i) => (
            <tr key={ss.id ?? i} style={{ background: i % 2 === 0 ? '#1e2e36' : '#1a2a32' }}>
              <td style={{ padding: '5px 10px', borderBottom: '1px solid #263238', color: '#b0bec5', whiteSpace: 'nowrap' }}>
                {ss.memberName || ss.memberIdentifier || '—'}
              </td>
              <td style={{ padding: '5px 10px', borderBottom: '1px solid #263238' }}>
                {ss.ticketInfo
                  ? <span style={{ color: '#90caf9' }}>{ss.ticketInfo}</span>
                  : ss.projectInfo
                    ? <span style={{ color: '#cfd8dc' }}>{ss.projectInfo}</span>
                    : <span style={{ color: '#546e7a' }}>—</span>}
              </td>
              <td style={{ padding: '5px 10px', borderBottom: '1px solid #263238', color: '#78909c', whiteSpace: 'nowrap' }}>
                {formatShortDate(ss.dateStart)}
                {ss.dateEnd && ss.dateEnd !== ss.dateStart ? <> – {formatShortDate(ss.dateEnd)}</> : ''}
              </td>
              <td style={{ padding: '5px 10px', borderBottom: '1px solid #263238', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {ss.hours != null && Number(ss.hours) > 0
                  ? Number(ss.hours).toFixed(1)
                  : <span style={{ color: '#546e7a' }}>—</span>}
              </td>
              {onEditRow && (
                <td style={{ padding: '4px 8px', borderBottom: '1px solid #263238', textAlign: 'right' }}>
                  <button
                    onClick={() => onEditRow(ss)}
                    style={{ padding: '2px 8px', background: '#263238', border: '1px solid #455a64', borderRadius: 4, color: '#90caf9', cursor: 'pointer', fontSize: 11 }}
                  >
                    Edit
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Single-week section (renders one week's data independently) ───────────────

function WeekSection({ weekStartIso, token, memberIdentifiers, projectFilter, users, activeSubTab, onAddResource, onAddResourceForTicket, onAddSoftScheduleForPerson, onEditSoftScheduleRow, softVersion, pendingAdditions = [], refreshVersion, colorRules, selectedRuleIds, sortCol, sortDir, onSort, showTicketDetails, hiddenStatuses, onStatusesDiscovered }) {
  const { loading, error, ticketRows, personRows, softScheduleRows } = useUtilizationData({
    token,
    weekStartIso,
    memberIdentifiers,
    projectFilter,
    users,
    enabled: !!token && !!weekStartIso,
    version: refreshVersion,
    softVersion,
  });

  const weekStart = new Date(weekStartIso + 'T00:00:00');
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const relevant = pendingAdditions.filter(a => {
    const d = new Date(a.dateStart + 'T00:00:00');
    return d >= weekStart && d <= weekEnd;
  });

  let mergedTicketRows = ticketRows;
  let mergedPersonRows = personRows;
  if (relevant.length > 0) {
    const rMap = new Map(ticketRows.map(r => [r.ticketId, { ...r, resources: [...r.resources] }]));
    relevant.forEach(a => {
      if (!rMap.has(a.ticketId)) {
        rMap.set(a.ticketId, {
          ticketId: a.ticketId, ticketNumber: a.ticketNumber,
          summary: a.ticketSummary, projectName: a.projectName,
          projectId: a.projectId, budgetHours: null,
          taskType: '', taskInfo: '', resources: [],
        });
      }
      const row = rMap.get(a.ticketId);
      const existing = row.resources.find(x => x.memberIdentifier === a.memberIdentifier);
      if (existing) { existing.scheduledHours += a.scheduledHours; }
      else { row.resources.push({ memberName: a.memberName, memberIdentifier: a.memberIdentifier, scheduledHours: a.scheduledHours, actualHours: 0 }); }
    });
    mergedTicketRows = Array.from(rMap.values()).sort((a, b) => (a.summary || '').localeCompare(b.summary || ''));

    const pMap = new Map(personRows.map(r => [r.memberIdentifier || r.memberName, { ...r }]));
    relevant.forEach(a => {
      const key = a.memberIdentifier || a.memberName;
      if (!pMap.has(key)) { pMap.set(key, { memberName: a.memberName, memberIdentifier: a.memberIdentifier, workType: '', scheduledHours: 0, actualHours: 0 }); }
      pMap.get(key).scheduledHours += a.scheduledHours;
    });
    mergedPersonRows = Array.from(pMap.values());
  }

  return (
    <div style={{ marginBottom: 20, border: '1px solid #37474f', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{
        padding: '8px 16px',
        background: '#1a2a32',
        borderBottom: '1px solid #37474f',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#90caf9', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Week of
        </span>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#e3f2fd' }}>
          {formatWeekDisplay(weekStartIso)}
        </span>
      </div>

      <div style={{ padding: '12px 16px' }}>
        {loading && <Loading />}
        {!loading && error && (
          <div style={{ color: '#ef5350', fontSize: 13, padding: '8px 0' }}>
            Error loading data: {error.message || String(error)}
          </div>
        )}
        {!loading && !error && (
          <>
            {activeSubTab === 'tickets' && (
              <>
                <TicketHoursView
                  ticketRows={mergedTicketRows}
                  onAddResource={onAddResource}
                  onAddResourceForTicket={onAddResourceForTicket}
                  colorRules={colorRules}
                  selectedRuleIds={selectedRuleIds}
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={onSort}
                  token={token}
                  showTicketDetails={showTicketDetails}
                  hiddenStatuses={hiddenStatuses}
                  onStatusesDiscovered={onStatusesDiscovered}
                />
                <SoftScheduleSection rows={softScheduleRows} onEditRow={onEditSoftScheduleRow} />
              </>
            )}
            {activeSubTab === 'persons' && (
              <PersonUtilizationView
                personRows={mergedPersonRows}
                onAddResource={onAddResource}
                onAddSoftScheduleForPerson={onAddSoftScheduleForPerson}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Inner tab (rendered after pre-filter commit) ──────────────────────────────

function UtilizationTabInner() {
  const {
    token,
    weekStartIso, setWeekStartIso,
    projectFilter,
    memberIdentifiers,
    users,
    committed, reset,
    canWrite,
    refreshVersion, lastRefreshed, refresh,
  } = useUtilizationContext();

  const [activeSubTab, setActiveSubTab] = useState('tickets'); // 'tickets' | 'persons'
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [ticketContext, setTicketContext] = useState(null);
  const [pendingAdditions, setPendingAdditions] = useState([]);

  // Soft schedule modal
  const [softModalOpen, setSoftModalOpen] = useState(false);
  const [softPresetTicket, setSoftPresetTicket] = useState(null);
  const [softPresetPerson, setSoftPresetPerson] = useState(null);
  const [softDefaultWeekStart, setSoftDefaultWeekStart] = useState(null);

  // Soft schedule edit modal
  const [softEditEntry, setSoftEditEntry] = useState(null);
  const [softEditModalOpen, setSoftEditModalOpen] = useState(false);

  // Incrementing this triggers only the soft schedule re-fetch (not a full page reload)
  const [softScheduleVersion, setSoftScheduleVersion] = useState(0);
  const bumpSoftVersion = () => setSoftScheduleVersion(v => v + 1);

  // Ticket filter/sort state — shared across all week sections
  const SHOW_HIGHLIGHTED = '__highlighted__';
  const [colorRules, setColorRules] = useState(() => getColorRules());
  const [selectedRuleIds, setSelectedRuleIds] = useState(() => new Set());
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [showOptions, setShowOptions] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef(null);

  // "Show Ticket Details" — reveals start/end date + status columns
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [discoveredStatuses, setDiscoveredStatuses] = useState(() => new Set());
  const [hiddenStatuses, setHiddenStatuses] = useState(() => new Set());
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef(null);

  const handleStatusesDiscovered = useCallback((statuses) => {
    setDiscoveredStatuses(prev => {
      let changed = false;
      const next = new Set(prev);
      statuses.forEach(s => { if (!next.has(s)) { next.add(s); changed = true; } });
      return changed ? next : prev;
    });
  }, []);

  function handleAddSuccess(addition) {
    setPendingAdditions(prev => [...prev, addition]);
  }

  function handleAddResourceForTicket(row) {
    setTicketContext({
      projectId: row.projectId,
      projectName: row.projectName,
      ticketId: row.ticketId,
      ticketNumber: row.ticketNumber,
      ticketSummary: row.summary,
    });
    setAddModalOpen(true);
  }

  // Close ticket filter dropdown on outside click
  useEffect(() => {
    function handleOutside(e) {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setFilterDropdownOpen(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(e.target)) {
        setStatusFilterOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleAddRule = useCallback(() => {
    const newRules = [...colorRules, { id: Date.now(), contains: '', color: '#1565c0' }];
    setColorRules(newRules);
    saveColorRules(newRules);
  }, [colorRules]);

  const handleDeleteRule = useCallback((id) => {
    const newRules = colorRules.filter(r => r.id !== id);
    setColorRules(newRules);
    saveColorRules(newRules);
    setSelectedRuleIds(prev => { const next = new Set(prev); next.delete(id); return next; });
  }, [colorRules]);

  const handleUpdateRule = useCallback((id, field, value) => {
    const newRules = colorRules.map(r => r.id === id ? { ...r, [field]: value } : r);
    setColorRules(newRules);
    saveColorRules(newRules);
  }, [colorRules]);

  function toggleRuleFilter(ruleId) {
    setSelectedRuleIds(prev => {
      const next = new Set(prev);
      next.has(ruleId) ? next.delete(ruleId) : next.add(ruleId);
      return next;
    });
  }

  function handleSort(col) {
    setSortCol(prev => {
      if (prev === col) {
        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        return col;
      }
      setSortDir('asc');
      return col;
    });
  }

  const filterLabel = selectedRuleIds.size === 0
    ? 'Show: All'
    : selectedRuleIds.size === 1 && selectedRuleIds.has(SHOW_HIGHLIGHTED)
      ? 'Show: Highlighted'
      : `Show: ${selectedRuleIds.size} filter${selectedRuleIds.size !== 1 ? 's' : ''}`;

  // 4 consecutive Sunday starts from the anchor week
  const weekStarts = Array.from({ length: 4 }, (_, i) => {
    const d = new Date(weekStartIso + 'T00:00:00');
    d.setDate(d.getDate() + i * 7);
    return getSundayIso(d);
  });

  const firstSun = new Date(weekStarts[0] + 'T00:00:00');
  const lastSat = new Date(weekStarts[3] + 'T00:00:00');
  lastSat.setDate(lastSat.getDate() + 6);
  const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const rangeLabel = `${fmt(firstSun)} – ${fmt(lastSat)}`;

  function handleWeekShift(delta) {
    const d = new Date(weekStartIso + 'T00:00:00');
    d.setDate(d.getDate() + delta * 7);
    setWeekStartIso(getSundayIso(d));
  }

  const S = {
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '10px 16px',
      background: '#1a2a32',
      borderBottom: '1px solid #37474f',
      flexWrap: 'wrap',
    },
    weekBtn: {
      padding: '5px 10px',
      background: '#37474f',
      border: '1px solid #546e7a',
      borderRadius: 5,
      color: '#eceff1',
      cursor: 'pointer',
      fontSize: 13,
    },
    weekLabel: { fontSize: 14, fontWeight: 600, color: '#e3f2fd', minWidth: 200, textAlign: 'center' },
    subtabs: { display: 'flex', gap: 0, borderBottom: '2px solid #37474f', marginBottom: 0 },
    subtab: (active) => ({
      padding: '9px 20px',
      background: active ? '#1565c0' : '#263238',
      color: active ? '#fff' : '#90caf9',
      border: 'none',
      borderBottom: active ? '2px solid #1565c0' : '2px solid transparent',
      cursor: 'pointer',
      fontWeight: active ? 700 : 400,
      fontSize: 13,
      marginBottom: -2,
    }),
    content: { padding: 16 },
    filterPill: {
      padding: '3px 10px',
      background: '#263238',
      border: '1px solid #455a64',
      borderRadius: 10,
      fontSize: 11,
      color: '#90caf9',
    },
    changeBtn: {
      marginLeft: 'auto',
      padding: '4px 10px',
      background: 'transparent',
      border: '1px solid #546e7a',
      borderRadius: 5,
      color: '#90caf9',
      cursor: 'pointer',
      fontSize: 12,
    },
  };

  return (
    <div style={{ fontFamily: 'inherit', color: '#eceff1' }}>
      {/* Navigation header — shows full 4-week range */}
      <div style={S.header}>
        <button style={S.weekBtn} onClick={() => handleWeekShift(-1)}>‹ Prev</button>
        <span style={S.weekLabel}>{rangeLabel}</span>
        <button style={S.weekBtn} onClick={() => handleWeekShift(1)}>Next ›</button>

        {projectFilter && (
          <span style={S.filterPill}>Project: {projectFilter}</span>
        )}
        {memberIdentifiers.length > 0 && (
          <span style={S.filterPill}>{memberIdentifiers.length} resource(s)</span>
        )}

        <button
          onClick={refresh}
          style={{
            marginLeft: 4,
            padding: '5px 12px',
            background: '#1b5e20',
            border: '1px solid #2e7d32',
            borderRadius: 5,
            color: '#fff',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
          }}
          title="Reload all utilization data"
        >
          Refresh
        </button>
        <span style={{ fontSize: 11, color: '#78909c' }}>
          Auto-refresh every 30 min
          {lastRefreshed
            ? ` · Last updated: ${new Date(lastRefreshed).toLocaleTimeString()}`
            : ''}
        </span>

        <button style={S.changeBtn} onClick={reset}>Change Filters</button>
      </div>

      {/* Sub-tabs */}
      <div style={S.subtabs}>
        <button style={S.subtab(activeSubTab === 'tickets')} onClick={() => setActiveSubTab('tickets')}>
          Ticket Hours
        </button>
        <button style={S.subtab(activeSubTab === 'persons')} onClick={() => setActiveSubTab('persons')}>
          Per-Person Utilization
        </button>
      </div>

      {/* Ticket filter toolbar — single controls above all week sections */}
      {activeSubTab === 'tickets' && (
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #37474f', background: '#1a2a32' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div ref={filterDropdownRef} style={{ position: 'relative' }}>
              <button
                style={{ padding: '5px 12px', background: '#263238', border: selectedRuleIds.size > 0 ? '1px solid #90caf9' : '1px solid #37474f', borderRadius: 5, color: selectedRuleIds.size > 0 ? '#90caf9' : '#eceff1', cursor: 'pointer', fontSize: 12 }}
                onClick={() => setFilterDropdownOpen(o => !o)}
              >
                ▾ {filterLabel}
              </button>
              {filterDropdownOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#1a2a32', border: '1px solid #37474f', borderRadius: 6, padding: '8px 0', minWidth: 220, boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#e3f2fd' }}>
                    <input type="checkbox" checked={selectedRuleIds.size === 0} onChange={() => setSelectedRuleIds(new Set())} style={{ cursor: 'pointer' }} />
                    <span style={{ fontWeight: 600 }}>Show All</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#e3f2fd' }}>
                    <input type="checkbox" checked={selectedRuleIds.has(SHOW_HIGHLIGHTED)} onChange={() => toggleRuleFilter(SHOW_HIGHLIGHTED)} style={{ cursor: 'pointer' }} />
                    <span style={{ color: '#ff8f00' }}>★</span>
                    <span>Show Highlighted</span>
                  </label>
                  {colorRules.length > 0 && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#e3f2fd' }}>
                      <input
                        type="checkbox"
                        checked={colorRules.length > 0 && colorRules.every(r => selectedRuleIds.has(r.id))}
                        ref={el => { if (el) el.indeterminate = colorRules.some(r => selectedRuleIds.has(r.id)) && !colorRules.every(r => selectedRuleIds.has(r.id)); }}
                        onChange={() => {
                          const allSelected = colorRules.every(r => selectedRuleIds.has(r.id));
                          setSelectedRuleIds(prev => {
                            const next = new Set(prev);
                            if (allSelected) { colorRules.forEach(r => next.delete(r.id)); }
                            else { colorRules.forEach(r => next.add(r.id)); }
                            return next;
                          });
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: 600 }}>All Color Highlights</span>
                    </label>
                  )}
                  <div style={{ borderTop: '1px solid #37474f', margin: '4px 0' }} />
                  {colorRules.map(rule => (
                    <label key={rule.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#e3f2fd' }}>
                      <input type="checkbox" checked={selectedRuleIds.has(rule.id)} onChange={() => toggleRuleFilter(rule.id)} style={{ cursor: 'pointer' }} />
                      <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: rule.color, flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {rule.contains || <em style={{ color: '#78909c' }}>(no text)</em>}
                      </span>
                    </label>
                  ))}
                  {colorRules.length === 0 && (
                    <div style={{ padding: '5px 12px', fontSize: 12, color: '#78909c' }}>No highlight rules defined.</div>
                  )}
                </div>
              )}
            </div>
            <button
              style={{ padding: '5px 12px', background: showOptions ? '#1a3a4a' : '#263238', border: showOptions ? '1px solid #90caf9' : '1px solid #37474f', borderRadius: 5, color: showOptions ? '#90caf9' : '#eceff1', cursor: 'pointer', fontSize: 12 }}
              onClick={() => setShowOptions(s => !s)}
            >
              ⚙ Options
            </button>

            {/* Show Ticket Details toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: showTicketDetails ? '#90caf9' : '#b0bec5', userSelect: 'none', marginLeft: 4 }}>
              <input
                type="checkbox"
                checked={showTicketDetails}
                onChange={e => { setShowTicketDetails(e.target.checked); if (!e.target.checked) { setHiddenStatuses(new Set()); setStatusFilterOpen(false); } }}
                style={{ cursor: 'pointer' }}
              />
              Show Ticket Details
            </label>

            {/* General soft schedule button */}
            {canWrite && (
              <button
                style={{ padding: '5px 12px', background: '#1a3a3a', border: '1px solid #37474f', borderRadius: 5, color: '#80cbc4', cursor: 'pointer', fontSize: 12, marginLeft: 4 }}
                onClick={() => { setSoftPresetTicket(null); setSoftPresetPerson(null); setSoftDefaultWeekStart(weekStartIso); setSoftModalOpen(true); }}
              >
                + Soft Schedule
              </button>
            )}

            {/* Status filter — only visible when Show Ticket Details is on */}
            {showTicketDetails && (
              <div ref={statusFilterRef} style={{ position: 'relative' }}>
                <button
                  style={{ padding: '5px 12px', background: '#263238', border: hiddenStatuses.size > 0 ? '1px solid #ef9a9a' : '1px solid #37474f', borderRadius: 5, color: hiddenStatuses.size > 0 ? '#ef9a9a' : '#eceff1', cursor: 'pointer', fontSize: 12 }}
                  onClick={() => setStatusFilterOpen(o => !o)}
                >
                  ▾ Status{hiddenStatuses.size > 0 ? ` (${hiddenStatuses.size} hidden)` : ''}
                </button>
                {statusFilterOpen && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#1a2a32', border: '1px solid #37474f', borderRadius: 6, padding: '8px 0', minWidth: 200, boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
                    {discoveredStatuses.size === 0 && (
                      <div style={{ padding: '6px 12px', fontSize: 12, color: '#78909c' }}>No statuses loaded yet.</div>
                    )}
                    {Array.from(discoveredStatuses).sort().map(status => (
                      <label key={status} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#e3f2fd' }}>
                        <input
                          type="checkbox"
                          checked={!hiddenStatuses.has(status)}
                          onChange={() => setHiddenStatuses(prev => {
                            const next = new Set(prev);
                            next.has(status) ? next.delete(status) : next.add(status);
                            return next;
                          })}
                          style={{ cursor: 'pointer' }}
                        />
                        {status}
                      </label>
                    ))}
                    {discoveredStatuses.size > 0 && (
                      <div style={{ borderTop: '1px solid #37474f', margin: '4px 0', padding: '4px 12px' }}>
                        <button onClick={() => setHiddenStatuses(new Set())} style={{ background: 'none', border: 'none', color: '#90caf9', cursor: 'pointer', fontSize: 11, padding: 0 }}>Show all</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {showOptions && (
            <div style={{ background: '#263238', border: '1px solid #37474f', borderRadius: 6, padding: '12px 16px', marginTop: 8 }}>
              <div style={{ color: '#90caf9', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Color Rules</div>
              {colorRules.length === 0 && (
                <div style={{ color: '#546e7a', fontSize: 12, marginBottom: 8 }}>No rules yet. Add a rule to highlight rows by summary text.</div>
              )}
              {colorRules.map(rule => (
                <div key={rule.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <input type="color" value={rule.color} onChange={e => handleUpdateRule(rule.id, 'color', e.target.value)} style={{ cursor: 'pointer', height: 28, width: 36, border: '1px solid #37474f', borderRadius: 4, padding: 2 }} title="Row highlight color" />
                  <input type="text" placeholder="contains text (matches summary)..." value={rule.contains} onChange={e => handleUpdateRule(rule.id, 'contains', e.target.value)} style={{ flex: 1, background: '#1a2a32', border: '1px solid #37474f', borderRadius: 4, color: '#eceff1', padding: '4px 8px', fontSize: 12, outline: 'none' }} />
                  <button onClick={() => handleDeleteRule(rule.id)} style={{ background: 'none', border: '1px solid #546e7a', borderRadius: 4, color: '#ef9a9a', cursor: 'pointer', padding: '2px 8px', fontSize: 13, lineHeight: 1 }} title="Remove rule">×</button>
                </div>
              ))}
              <button onClick={handleAddRule} style={{ padding: '5px 12px', background: '#37474f', border: '1px solid #546e7a', borderRadius: 5, color: '#eceff1', cursor: 'pointer', fontSize: 12, marginTop: 2 }}>+ Add Rule</button>
            </div>
          )}
        </div>
      )}

      {/* 4 week sections */}
      <div style={S.content}>
        {weekStarts.map(ws => (
          <WeekSection
            key={ws}
            weekStartIso={ws}
            token={token}
            memberIdentifiers={memberIdentifiers}
            projectFilter={projectFilter}
            users={users}
            activeSubTab={activeSubTab}
            onAddResource={canWrite ? () => setAddModalOpen(true) : undefined}
            onAddResourceForTicket={canWrite ? handleAddResourceForTicket : undefined}
            onAddSoftScheduleForPerson={canWrite ? (row) => {
              setSoftPresetPerson({ memberIdentifier: row.memberIdentifier, memberName: row.memberName });
              setSoftPresetTicket(null);
              setSoftDefaultWeekStart(ws);
              setSoftModalOpen(true);
            } : undefined}
            onEditSoftScheduleRow={canWrite ? (row) => {
              setSoftEditEntry(row);
              setSoftEditModalOpen(true);
            } : undefined}
            softVersion={softScheduleVersion}
            pendingAdditions={pendingAdditions}
            refreshVersion={refreshVersion}
            colorRules={colorRules}
            selectedRuleIds={selectedRuleIds}
            sortCol={sortCol}
            sortDir={sortDir}
            onSort={handleSort}
            showTicketDetails={showTicketDetails}
            hiddenStatuses={hiddenStatuses}
            onStatusesDiscovered={handleStatusesDiscovered}
          />
        ))}
      </div>

      <UtilizationAddModal
        open={addModalOpen}
        onClose={() => { setAddModalOpen(false); setTicketContext(null); }}
        onSuccess={handleAddSuccess}
        token={token}
        loadedTickets={[]}
        presetTicket={ticketContext}
      />

      <UtilizationSoftScheduleEditModal
        open={softEditModalOpen}
        onClose={() => { setSoftEditModalOpen(false); setSoftEditEntry(null); }}
        onSuccess={bumpSoftVersion}
        token={token}
        entry={softEditEntry}
      />

      <UtilizationSoftScheduleModal
        open={softModalOpen}
        onClose={() => setSoftModalOpen(false)}
        onSuccess={bumpSoftVersion}
        token={token}
        presetTicket={softPresetTicket}
        presetPerson={softPresetPerson}
        defaultDateStart={softDefaultWeekStart}
      />
    </div>
  );
}

// ── Top-level tab (handles pre-filter gate) ───────────────────────────────────

function UtilizationTabGated() {
  const { committed } = useUtilizationContext();
  return committed ? <UtilizationTabInner /> : <UtilizationPreFilter />;
}

export default function UtilizationTab() {
  return (
    <ErrorBoundary>
      <UtilizationDataProvider>
        <UtilizationTabGated />
      </UtilizationDataProvider>
    </ErrorBoundary>
  );
}
